import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Sentry } from "@/lib/sentry";

/**
 * Compute a stable, low-cardinality `dash.section` value from a pathname.
 *
 *   /dash                  → "dash"
 *   /dash/                 → "dash"
 *   /dash/verify           → "dash/verify"
 *   /dash/admin            → "dash/admin"
 *   /dash/admin/qa         → "dash/admin"   (collapsed · IDs/sub-pages don't fan out)
 *   /dash/onboarding       → "dash/onboarding"
 *   anything else          → null           (not a dashboard route)
 *
 * Exported for tests and for any non-React caller that wants to derive the
 * same tag value.
 */
export function dashSectionFor(pathname: string): string | null {
  if (!pathname || !pathname.startsWith("/dash")) return null;
  const parts = pathname.split("/").filter(Boolean); // ["dash", "admin", "qa"]
  if (parts[0] !== "dash") return null;
  return parts.slice(0, 2).join("/") || "dash";
}

/**
 * The dash route the user was on BEFORE the current one, session-scoped.
 * Feeds the feedback form's page_url so a bug report points at the page the
 * bug happened on, not at /dash/feedback itself. Carries the query string
 * (filters, entity ids) — consumers comparing against a pathname must split
 * on "?". Null on a direct load with no prior in-tab navigation.
 */
const PREV_DASH_ROUTE_KEY = "cr_prev_dash_route";
export function previousDashRoute(): string | null {
  try {
    return sessionStorage.getItem(PREV_DASH_ROUTE_KEY);
  } catch {
    return null;
  }
}

/**
 * Tags every `/dash/*` navigation with `dash.section` + `dash.route` and
 * emits a `navigation.dash` breadcrumb. Mount ONCE at the App level (above
 * <Routes/>) so it covers every dashboard page · including ones that render
 * outside DashV2Layout (e.g. `/dash/onboarding`) or any future top-level
 * dashboard page that bypasses the shared shell.
 *
 * Also clears the tags when the user navigates *away* from /dash so
 * subsequent errors on landing/marketing pages aren't mis-attributed.
 */
export function DashRouteTracer() {
  const { pathname, search } = useLocation();
  const prevRef = useRef<string | null>(null);
  // Full path (pathname + search) sibling of prevRef, feeding the feedback
  // page_url capture — bugs are often about page STATE carried in the query.
  const prevFullRef = useRef<string | null>(null);
  const lastTaggedRef = useRef<string | null>(null);

  useEffect(() => {
    const section = dashSectionFor(pathname);
    const scope = Sentry.getCurrentScope();

    // Left the dashboard · clear tags so non-dash errors don't carry stale context.
    if (!section) {
      if (lastTaggedRef.current !== null) {
        scope.setTag("dash.section", undefined as unknown as string);
        scope.setTag("dash.route", undefined as unknown as string);
        lastTaggedRef.current = null;
      }
      // Don't touch prevRef · the next dash entry should still see "from = last dash route".
      return;
    }

    const from = prevRef.current;
    const to = pathname;
    prevRef.current = to;

    const fromFull = prevFullRef.current;
    prevFullRef.current = pathname + (search || "");

    // Store only on a PAGE change (same-page search churn must not clobber the
    // remembered previous page with the current one).
    if (fromFull && fromFull.split("?")[0] !== pathname) {
      try {
        sessionStorage.setItem(PREV_DASH_ROUTE_KEY, fromFull);
      } catch {
        // storage unavailable — feedback falls back to the form URL
      }
    }

    Sentry.addBreadcrumb({
      category: "navigation.dash",
      type: "navigation",
      level: "info",
      message: from ? `${from} → ${to}` : `enter ${to}`,
      data: { from, to, search: search || undefined, section },
    });

    scope.setTag("dash.section", section);
    scope.setTag("dash.route", to);
    lastTaggedRef.current = to;

    // Name the active root span (created by the router tracing integration)
    // after the dashboard section, e.g. "dash/admin · /dash/admin/qa".
    const span = Sentry.getActiveSpan();
    const root = span ? Sentry.getRootSpan(span) : undefined;
    if (root) {
      root.updateName(`${section} · ${to}`);
      root.setAttribute("dash.section", section);
      root.setAttribute("dash.route", to);
    }
  }, [pathname, search]);

  return null;
}
